import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { CALCULATORS, CATEGORIES, calculatorsByCategory } from "@/lib/calculators";
import { useT } from "@/lib/i18n";
import { useApp } from "@/lib/appContext";
import { STATES } from "@/lib/states";
import AdSlot from "@/components/AdSlot";
import { useSeo } from "@/lib/seo";

export default function Home() {
  const t = useT();
  const { state, lang } = useApp();
  const stateObj = STATES.find((s) => s.code === state);
  const stateLabel = lang === "hi" ? stateObj?.hi : stateObj?.name;
  const featured = CALCULATORS.filter((c) => c.featured);

  useSeo({
    title: "India Land, Property & Real Estate Calculators",
    description: "LandCalc — India's most comprehensive state-aware land calculator suite. Convert Bigha, Katha, Ground, Ankanam; estimate stamp duty, EMI, property tax and construction cost.",
    path: "/",
  });

  return (
    <div data-testid="home-dashboard">
      <section className="relative overflow-hidden rounded-xl border border-border bg-card mb-8 md:mb-12">
        <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759269834665-2b401eb4d6f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRvcG9ncmFwaGljJTIwbWFwJTIwcGF0dGVybnxlbnwwfHx8fDE3ODI5MTg1MjV8MA&ixlib=rb-4.1.0&q=85')", backgroundSize:"cover", backgroundPosition:"center" }}
        />
        <div className="relative p-6 md:p-10 lg:p-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold tracking-wide mb-4">
              <Sparkles className="h-3 w-3" /> {CALCULATORS.length} CALCULATORS · {stateLabel?.toUpperCase()}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              {t("hero_title").split(" land calculator ")[0]}
              <br />
              <span className="text-primary">land calculator</span>
              <span className="text-saffron"> suite.</span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl">{t("hero_sub")}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/land-converter" data-testid="hero-cta-converter" className="h-11 px-5 rounded-md bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-1.5 hover:bg-primary/90 transition">
                {t("open")} — Unit Converter <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/stamp-duty" data-testid="hero-cta-stamp" className="h-11 px-5 rounded-md border border-border bg-card text-sm font-semibold flex items-center gap-1.5 hover:bg-accent transition">
                Stamp Duty · {stateObj?.code}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-3 gap-3">
            {[{k:STATES.length,l:"States"},{k:CALCULATORS.length,l:"Tools"},{k:"EN/हिं",l:"Bilingual"}].map((s)=>(
              <div key={s.l} className="rounded-lg border border-border bg-background/70 backdrop-blur p-3">
                <div className="text-2xl font-black tracking-tighter text-primary stat-num">{s.k}</div>
                <div className="overline mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{t("popular")}</h2>
        <span className="overline">Start here</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="featured-tiles">
        {featured.map((c) => <Tile key={c.slug} calc={c} lang={lang} featured />)}
      </div>

      <AdSlot slot="home-mid" className="mb-10" testId="ad-home-mid" />

      {CATEGORIES.map((cat) => {
        const items = calculatorsByCategory(cat.key);
        if (!items.length) return null;
        return (
          <section key={cat.key} className="mb-10" data-testid={`category-${cat.key}`}>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                {lang === "hi" ? cat.hi : cat.label}
                <span className={`ml-2 inline-block w-2 h-2 rounded-full ${cat.accent==="saffron" ? "bg-saffron" : "bg-primary"}`}></span>
              </h2>
              <span className="overline">{items.length} tools</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {items.map((c) => <Tile key={c.slug} calc={c} lang={lang} accent={cat.accent} />)}
            </div>
          </section>
        );
      })}

      <AdSlot slot="home-footer" testId="ad-home-footer" />
    </div>
  );
}

function Tile({ calc, featured, lang, accent }) {
  const { Icon } = calc;
  const isSaffron = accent === "saffron";
  return (
    <Link to={`/${calc.slug}`} data-testid={`tile-${calc.slug}`}
      className={`tile-lift group flex flex-col justify-between p-5 sm:p-6 rounded-lg border ${featured ? "border-primary/30 bg-primary/[0.03]" : "border-border bg-card"} shadow-sm hover:shadow-md hover:border-primary/40 min-h-[168px]`}>
      <div>
        <div className={`w-10 h-10 rounded-md ${isSaffron ? "bg-saffron/15 text-saffron" : "bg-primary/10 text-primary"} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="font-bold text-base tracking-tight">{lang==="hi" ? calc.hi : calc.title}</div>
        <div className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{calc.description}</div>
      </div>
      <div className="flex items-center justify-between mt-4">
        <span className={`overline ${isSaffron ? "text-saffron/90" : "text-primary/80"}`}>Open</span>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </Link>
  );
}
